import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Exception } from '@cargos/sprintpay-models';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import dayjs from 'dayjs';
import { Subject, take } from 'rxjs';
import { PaymentCartTypeType } from 'src/app/shared/models/payment-type';
import { PaymentCartTypeHandlerService } from 'src/app/shared/services/payment-cart-type-handler.service';
import Swal from 'sweetalert2';
import { SearchType } from '../../../models/search-type';
import { SummaryService } from '../../../services';
import { SearchByFlightService } from '../../../services/search-by-flight.service';
import { FlightNumberSearchFormErrorJSON, FlightNumberSearchFormGroupType } from './models/flight-number-search-form';
import { formFlightNumberSearchErrors } from './utils/form-errors';

@Component({
    selector: 'app-flight-number-search',
    templateUrl: './flight-number-search.component.html',
    styleUrls: ['./flight-number-search.component.scss'],
    standalone: false,
})
export class FlightNumberSearchComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    private _mobileQueryListener: () => void;

    public mobileQuery: MediaQueryList;
    public searchForm: FlightNumberSearchFormGroupType;
    public isLoading = false;
    public isCompleted = false;
    public minDateAllowed: Date;
    public formErrors: FlightNumberSearchFormErrorJSON;

    @Input() disabled = false;

    @Output() eventOnEnter = new EventEmitter();
    @Output() eventOnSearching = new EventEmitter<boolean>();

    get flightNumber(): AbstractControl<string> | null {
        return this.searchForm.get('flightNumber');
    }

    get date(): AbstractControl<string> | null {
        return this.searchForm.get('date');
    }

    constructor(
        private fb: NonNullableFormBuilder,
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private router: Router,
        private searchByFlightService: SearchByFlightService,
        private summaryService: SummaryService,
        private paymentCartTypeHandlerService: PaymentCartTypeHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();

        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
        this.formErrors = formFlightNumberSearchErrors;
    }

    ngOnInit(): void {
        this.createForm();
        this.setDate();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    }

    setDate(): void {
        const currentdate = new Date();
        this.minDateAllowed = currentdate;
    }

    createForm(): void {
        this.searchForm = this.fb.group({
            flightNumber: ['', [Validators.required, CustomValidators.regex(new RegExp(CustomPatterns.FLIGHT_NUMBER))]],
            date: ['', [Validators.required]],
        });
    }

    submit(): void {
        if (this.isLoading) {
            return;
        }
        if (this.flightNumber?.invalid || this.date?.invalid) {
            this.flightNumber?.markAllAsTouched();
            this.date?.markAllAsTouched();
            return;
        }
        this.eventOnEnter.emit();
        this.eventOnSearching.emit(true);
        this.isLoading = true;
        const flightNumberValue = this.flightNumber?.value?.toLocaleUpperCase() || '';
        const flightDate = dayjs(this.date?.value).format('YYYY-MM-DD');
        const pickupDate = dayjs().format('YYYY-MM-DD');

        this.searchByFlightService
            .getChargeByFlight(flightNumberValue, flightDate, pickupDate)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.eventOnSearching.emit(false);
                    this.isLoading = false;

                    this.summaryService.setAndSaveSearchTypeInStorage(SearchType.flight);
                    this.paymentCartTypeHandlerService.setPaymentCartType(PaymentCartTypeType.MAWB);

                    this.router.navigate(['search-flight/details']);
                },
                error: (error: Exception) => {
                    this.isLoading = false;
                    this.eventOnSearching.emit(false);

                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: error.type,
                    });
                },
            });
    }
}
